import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { authStore } from '@/stores/authStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert } from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import { GoogleIcon, MicrosoftIcon } from '@/components/icons/index';
import PasswordStrengthBar from '@/components/PasswordStrengthBar';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

const RegisterPage = observer(() => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordTips, setPasswordTips] = useState<string[]>([]);
  const { t } = useTranslation();

  const validatePassword = (pass: string) => {
    const tips: string[] = [];
    let strength = 0;

    // Length check
    if (pass.length < 8) {
      tips.push('Password should be at least 8 characters long');
    } else {
      strength += 20;
    }

    // Uppercase check
    if (!/[A-Z]/.test(pass)) {
      tips.push('Include at least one uppercase letter');
    } else {
      strength += 20;
    }

    // Lowercase check
    if (!/[a-z]/.test(pass)) {
      tips.push('Include at least one lowercase letter');
    } else {
      strength += 20;
    }

    // Number check
    if (!/\d/.test(pass)) {
      tips.push('Include at least one number');
    } else {
      strength += 20;
    }

    // Special character check
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pass)) {
      tips.push('Include at least one special character');
    } else {
      strength += 20;
    }

    setPasswordStrength(strength);
    setPasswordTips(tips);
  };

  useEffect(() => {
    validatePassword(password);
  }, [password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    if (passwordStrength < 60) {
      setPasswordError('Please create a stronger password');
      return;
    }

    try {
      await authStore.register(email, password, name);
      navigate('/dashboard');
    } catch (error) {
      // Error is handled by the store
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="max-w-md w-full space-y-8 p-6">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('auth.register')}
          </h2>
          <div className="flex justify-center mt-4">
            <LanguageSwitcher />
          </div>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm space-y-4">
            <div>
              <Input
                type="text"
                required
                placeholder={t('account.firstName') + ' ' + t('account.lastName')}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <Input
                type="email"
                required
                placeholder={t('auth.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <Input
                type="password"
                required
                placeholder={t('auth.password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <PasswordStrengthBar strength={passwordStrength} />
              {passwordTips.length > 0 && (
                <div className="mt-2 text-sm text-gray-600">
                  <p className="font-medium">Password requirements:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    {passwordTips.map((tip, index) => (
                      <li key={index}>{tip}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div>
              <Input
                type="password"
                required
                placeholder={t('auth.confirmPassword')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>

          {(authStore.error || passwordError) && (
            <Alert variant="destructive">
              {authStore.error || passwordError}
            </Alert>
          )}

          <div>
            <Button
              type="submit"
              className="w-full"
              disabled={authStore.loading}
            >
              {authStore.loading ? t('common.loading') : t('auth.signUp')}
            </Button>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                Or continue with
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => authStore.googleLogin()}
              disabled={authStore.loading}
            >
              <GoogleIcon className="mr-2 h-4 w-4" />
              Google
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => authStore.microsoftLogin()}
              disabled={authStore.loading}
            >
              <MicrosoftIcon className="mr-2 h-4 w-4" />
              Microsoft
            </Button>
          </div>
        </form>

        <p className="mt-2 text-center text-sm text-gray-600">
          {t('auth.alreadyHaveAccount')}{' '}
          <Link
            to="/login"
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            {t('auth.signIn')}
          </Link>
        </p>
      </Card>
    </div>
  );
});

export default RegisterPage; 